import { useEffect, useRef, useState } from 'react';
import { api, type SpanTraza } from '../api';
import { Cascada } from './Cascada';

/**
 * Una traza en una ventana, para abrirla desde donde se la encontró.
 *
 * El resumen y el buscador llegan a una ejecución concreta sin pasar por el
 * proceso al que pertenece. Obligar a ir a «Procesos», elegir el proceso y
 * buscar la ejecución en la tabla para ver la cascada era justo el recorrido
 * que esas pantallas existen para ahorrar.
 *
 * Solo para quien puede ver servicios: la cascada nombra servicios internos
 * del cliente, y la ruta del portal lo exige igual.
 */
export function TrazaModal({
  traceId,
  puedeDescargar,
  onCerrar,
  onError,
}: {
  traceId: string;
  puedeDescargar: boolean;
  onCerrar: () => void;
  onError: (err: unknown) => void;
}) {
  const [spans, setSpans] = useState<SpanTraza[] | null>(null);
  const dialogo = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let vigente = true;
    setSpans(null);
    api
      .traza(traceId)
      .then((r) => vigente && setSpans(r.spans))
      .catch((err) => {
        if (!vigente) return;
        onError(err);
        onCerrar();
      });
    return () => {
      vigente = false;
    };
  }, [traceId, onError, onCerrar]);

  // Escape cierra, y el foco entra al diálogo para que se pueda usar sin mouse.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
    };
    document.addEventListener('keydown', alTeclear);
    dialogo.current?.querySelector<HTMLElement>('button, a')?.focus();
    return () => document.removeEventListener('keydown', alTeclear);
  }, [onCerrar]);

  return (
    <div className="telon" onClick={onCerrar}>
      <div
        className="dialogo traza-modal"
        ref={dialogo}
        role="dialog"
        aria-modal="true"
        aria-label="Traza de la ejecución"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="traza-modal-cabecera">
          <h2>
            Traza <code>{traceId.slice(0, 16)}…</code>
          </h2>
          <div className="traza-modal-acciones">
            {puedeDescargar && (
              <a
                className="boton-enlace"
                href={`/informe/traza.md?traza=${encodeURIComponent(traceId)}`}
                title="Descargar el detalle de llamadas (Markdown)"
              >
                Descargar detalle
              </a>
            )}
            <button type="button" className="secundario" onClick={onCerrar}>
              Cerrar
            </button>
          </div>
        </header>
        {spans === null ? <p className="vacio">Cargando la traza…</p> : <Cascada spans={spans} />}
      </div>
    </div>
  );
}
